import { useCallback, useRef, ReactNode, useState } from "react";
import { DrawerProps, Button, Buttons, Drawer } from "capitalrx-components-2.0/lib/general";
import { Shelf } from "capitalrx-components-2.0/lib/layout";
import { FormProps, Form } from ".";
import { SubmitButton } from "../SubmitButton";
import { FormModalCloseEventHandler } from "./FormModalCloseEventHandler";
import styles from "./styles.module.scss";
import { FieldValues, UseFormReturn } from "react-hook-form";

interface FormDrawerProps<TValues extends FieldValues> extends FormProps<TValues>, Omit<DrawerProps, "onClose" | "footer" | "children"> {
  /**
   * The content within the submit button. If not specified defaults to "Submit".
   */
  readonly submitContent?: ReactNode;

  /**
   * The content within the cancel button. If not specified defaults to "Cancel".
   */
  readonly cancelContent?: ReactNode;

  /**
   * The content within the reset button. If not specified defaults to "Reset".
   */
  readonly resetContent?: ReactNode;

  /**
   * Called when the close button or cancel button are clicked, or submitting is complete.
   */
  readonly onClose: FormModalCloseEventHandler;

  /**
   * Indicates whether the form is able to be reset. If not specified defaults to `true`.
   */
  readonly canReset?: boolean;
}

/**
 * A component which displays a Form within a Drawer.
 */
export const FormDrawer = <TValues extends FieldValues = FieldValues>(props: FormDrawerProps<TValues>) => {
  const {
    className,
    style,
    testId,
    visible,
    title,
    children,
    position,
    submitContent = "Submit",
    cancelContent = "Cancel",
    resetContent = "Reset",
    onSubmit,
    onClose,
    canReset = true,
    size = "wide",
    methodsRef,
    ...formProps
  } = props;
  const internalRef = useRef<UseFormReturn<TValues> | undefined | null>(formProps.form || undefined);
  const [submitting, setSubmitting] = useState(false);

  const submitHandler = useCallback(
    async (values: TValues) => {
      setSubmitting(true);

      if (!onSubmit) {
        setSubmitting(false);
        onClose(true);
        return;
      }

      try {
        await onSubmit(values);
        onClose(true);
      } finally {
        setSubmitting(false);
      }
    },
    [onSubmit, onClose],
  );

  const resetHandler = useCallback(() => {
    if (!canReset) return;
    if (!internalRef.current) return;

    internalRef.current.reset();
  }, [canReset]);

  const cancelHandler = useCallback(() => {
    if (submitting) return;

    onClose(false);
  }, [submitting, onClose]);

  const internalMethodsRef = useCallback(
    (ref: UseFormReturn<TValues> | null) => {
      internalRef.current = ref;

      if (!methodsRef) return;
      if (typeof methodsRef === "function") {
        methodsRef(ref);
      } else {
        methodsRef.current = ref;
      }
    },
    [methodsRef],
  );

  return (
    <Drawer
      className={className}
      style={style}
      testId={testId}
      visible={visible}
      title={title}
      size={size}
      position={position}
      onClose={cancelHandler}
      root={(children) => (
        <Form className={styles.ModalForm} {...formProps} methodsRef={methodsRef ? internalMethodsRef : undefined} onSubmit={submitHandler}>
          {children}
        </Form>
      )}
      footer={
        <Shelf spacing="small">
          {canReset && (
            <Button appearance={Buttons.Functional.Tertiary.Large} onClick={resetHandler} disabled={submitting}>
              {resetContent}
            </Button>
          )}
          <Shelf.Spacer />
          <Button appearance={Buttons.Functional.Secondary.Large} onClick={cancelHandler} disabled={submitting}>
            {cancelContent}
          </Button>
          <SubmitButton appearance={Buttons.Functional.Primary.Large} autoFocus>
            {submitContent}
          </SubmitButton>
        </Shelf>
      }
    >
      <>{children}</>
    </Drawer>
  );
};
